// src/components/ImportSeedDialog.js
import React, { useState, useEffect } from 'react';

// Joy UI Components
import Modal from '@mui/joy/Modal';
import ModalDialog from '@mui/joy/ModalDialog';
import ModalClose from '@mui/joy/ModalClose';
import Input from '@mui/joy/Input';
import List from '@mui/joy/List';
import ListItem from '@mui/joy/ListItem';
import ListItemButton from '@mui/joy/ListItemButton';
import Avatar from '@mui/joy/Avatar';
import Typography from '@mui/joy/Typography';
import Box from '@mui/joy/Box';
import IconButton from '@mui/joy/IconButton';
import Divider from '@mui/joy/Divider';
import Sheet from '@mui/joy/Sheet';
import Button from '@mui/joy/Button';
import CircularProgress from '@mui/joy/CircularProgress';
import Chip from '@mui/joy/Chip';
import Stack from '@mui/joy/Stack';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import FilterListIcon from '@mui/icons-material/FilterList';
import NoPhotographyIcon from '@mui/icons-material/NoPhotography';
import SpaRoundedIcon from '@mui/icons-material/SpaRounded';
import { listSeeds } from '../api/allSeeds';
import { Grid, Card, CardContent } from '@mui/joy';

export default function ImportSeedDialog({
  open,
  onClose,
  onSelect,
  inventoryType  // pass through from InventoryTable's `type` prop
}) {
  const [seeds, setSeeds] = useState([]);
  const [filteredSeeds, setFilteredSeeds] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCollection, setSelectedCollection] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Get type-specific color
  const getTypeColor = () => {
    switch(inventoryType) {
      case 'PRODUCT-SEED': return '#4caf50';
      case 'PRODUCT-CLONE': return '#ba68c8';
      default: return '#2196f3';
    }
  };
  
  const typeColor = getTypeColor();
  
  // Get type-specific title
  const getDialogTitle = () => {
    switch(inventoryType) {
      case 'PRODUCT-SEED': return 'Import Seeds';
      case 'PRODUCT-CLONE': return 'Import Clones';
      default: return 'Import Items';
    }
  };

  useEffect(() => {
    async function fetchSeeds() {
      try {
        const allSeeds = await listSeeds();
        setSeeds(allSeeds);
        setFilteredSeeds(allSeeds);
      } catch (error) {
        console.error('Failed to fetch seeds:', error);
      }
    }
    if (open) fetchSeeds();
  }, [open]);

  // Modify collection filtering logic
  useEffect(() => {
    let result = seeds;

    // Filter by search term
    if (searchTerm) {
      const lowercaseTerm = searchTerm.toLowerCase().trim();
      result = result.filter(seed => 
        seed.name.toLowerCase().includes(lowercaseTerm) ||
        seed.description.toLowerCase().includes(lowercaseTerm) ||
        seed.collection.toLowerCase().includes(lowercaseTerm)
  );
    }

    // If inventory type is PRODUCT-CLONE, only show seeds with 'clone' in collection
    if (inventoryType === 'PRODUCT-CLONE') {
      result = result.filter(seed => 
        seed.collection?.toLowerCase().includes('clone')
      );
    } else if (inventoryType === 'PRODUCT-SEED') {
      // For seed inventory, exclude clones
      result = result.filter(seed => 
        !seed.collection?.toLowerCase().includes('clone')
      );
    }

    setFilteredSeeds(result);
  }, [seeds, searchTerm, inventoryType]);

  const collections = Array.from(new Set(
    seeds
      .map(seed => seed.collection)
      .filter(collection => collection && collection.trim() !== '')
  ));

  return (
    <Modal
      open={open} 
      onClose={onClose} 
      sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        '@media (max-width: 900px)': {
          alignItems: 'flex-end'
        }
      }}
    >
      <ModalDialog
        variant="soft"
        sx={{ 
          width: '100vw',
          height: '100vh',
          maxWidth: 'none',
          maxHeight: 'none',
          m: 0,
          borderRadius: { xs: 0, md: 'xl' },
          overflow: 'auto',
          background: 'rgba(0, 0, 0, 0.95)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.1)'
        }}
      >
        <ModalClose />
        
        {/* Update the modal title and header */}
        <Box sx={{ 
          textAlign: 'center', 
          mb: 3,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}>
          <Typography level="h3" sx={{ color: 'white', mb: 1 }}>
            {getDialogTitle()}
          </Typography>
          <Typography level="body-md" sx={{ color: 'text.secondary', maxWidth: 400, textAlign: 'center' }}>
            {inventoryType === 'PRODUCT-SEED' 
              ? 'Select seeds to import into your inventory' 
              : 'Select clones to import into your inventory'}
          </Typography>
        </Box>

        <Box sx={{ p: 3 }}>
          <Input
          placeholder="Search by name, description, or collection..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            startDecorator={<SearchIcon />}
            sx={{ 
              mb: 3,
              borderRadius: 'md',
              bgcolor: 'rgba(255, 255, 255, 0.05)'
            }}
          />

          {/* Update the loading/content section */}
        {loading ? (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            minHeight: 300 
          }}>
              <CircularProgress size="lg" />
          </Box>
        ) : (
            <Box>
              {filteredSeeds.length > 0 ? (
                <Grid container spacing={2}>
                  {filteredSeeds.map((seed) => (
                    <Grid key={seed.id} xs={12} sm={6} md={4}>
                      <Card 
                        variant="soft" 
                    sx={{ 
                          cursor: 'pointer', 
                        '&:hover': {
                            boxShadow: `0 4px 14px rgba(${
                              inventoryType === 'PRODUCT-SEED' ? '87, 200, 77' : '188, 19, 254'
                            }, 0.3)` 
                        }
                      }}
                        onClick={() => onSelect(seed)}
                      >
                        {seed.imageUrl && (
                          <Box
                            component="img"
                            src={seed.imageUrl}
                            alt={seed.name}
                            sx={{ 
                              width: '100%',
                              height: 200,
                              objectFit: 'cover'
                            }}
                          />
                        )}
                        <CardContent sx={{ textAlign: 'center' }}>
                          <Typography level="title-md" sx={{ textAlign: 'center', fontWeight: 'bold', mb: 1 }}>
                            {seed.name}
                          </Typography>
                          <Typography level="body-sm" sx={{ color: 'text.secondary', textAlign: 'center', mb: 1 }}>
                            {seed.description}
                          </Typography>
                          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                            <Chip variant="soft" size="sm">
                              {seed.collection}
                            </Chip>
                          </Box>
                          <Typography level="body-sm" sx={{ textAlign: 'center' }}>
                            Inventory: {seed.inventory}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Sheet 
                  variant="soft" 
                              sx={{
                    p: 4, 
                    textAlign: 'center', 
                    bgcolor: 'rgba(255, 255, 255, 0.05)',
                    border: '1px dashed rgba(255, 255, 255, 0.2)'
                              }}
                            >
                  <Typography level="h4" sx={{ mb: 2 }}>
                    No {inventoryType === 'PRODUCT-SEED' ? 'Seeds' : 'Clones'} Found
                            </Typography>
                  <Typography level="body-md" sx={{ color: 'text.secondary' }}>
                    {inventoryType === 'PRODUCT-SEED' 
                      ? 'No seed products available to import.' 
                      : 'No clone products available to import.'}
                              </Typography>
                </Sheet>
              )}
          </Box>
        )}
        </Box>
      </ModalDialog>
    </Modal>
  );
}
